import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "../../../language/i18n";
import { Icon } from "../../../library";
import { useAuth } from "../../../context/UserContextProvider";
import {
  acceptDuelInvite,
  declineDuelInvite,
  joinDuelByCode,
  spectateDuelByCode,
  retrievePendingDuelInvites,
} from "../../../requests/CatchtivityRequests";
import { retrievePendingFriendRequests } from "../../../requests/ManagementRequests";
import { NotificationManager } from "../../../notifications/NotificationManager";
import duelSounds from "../../../utils/duelSounds";
import usePresenceStatusReporter, {
  statusForPathname,
} from "../../../hooks/usePresenceStatusReporter";
import FriendsOnlinePanel from "./FriendsOnlinePanel";
import DuelInviteToast from "./DuelInviteToast";
import { duelSocialStyles, friendPanelStyles } from "./duelSocialStyles";
import { FRIEND_EVENT, FriendEventPayload } from "./friendTypes";

// Any screen (e.g. the duel waiting room) can open the global Friends-online panel by dispatching this.
export const OPEN_FRIENDS_PANEL_EVENT = "cu:open-friends-panel";

// App-shell layer (mounted once for a logged-in learner) that makes duel-invites work from ANY screen:
// a persistent "Friends online" button + drawer, and a global listener on the per-user invite topic that
// pops a non-blocking toast wherever the learner is. Prefix: dso-.
//
// Invites are QUEUED (not a single slot): if several arrive at once, they're shown one at a time (newest
// waiting behind), so simultaneous challenges never overwrite each other. Accepting one auto-declines the
// rest (you can only be in one duel anyway), giving those inviters instant feedback.
const DuelSocialLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webSocketStompClient, userProfile, userInformation } = useAuth();

  const [panelOpen, setPanelOpen] = useState(false);
  const [inviteQueue, setInviteQueue] = useState<any[]>([]);
  const [incomingRequestCount, setIncomingRequestCount] = useState(0);
  const inviteQueueRef = useRef<any[]>([]);
  inviteQueueRef.current = inviteQueue;

  // Report which kind of screen the learner is on, so friends see "Soru çözüyor" rather than a bare
  // "Çevrimiçi" and the server can refuse an invite that would interrupt them.
  usePresenceStatusReporter();

  // Append invites de-duped by inviteId — shared by the live push and the recovery fetch so a
  // recovered invite that also arrives live (or vice-versa) is never shown twice.
  const enqueueInvites = (incoming: any[]) => {
    if (!incoming || incoming.length === 0) return;
    setInviteQueue((queue) => {
      const seen = new Set(queue.map((invite) => invite.inviteId));
      const fresh = incoming.filter(
        (invite) => invite?.inviteId && !seen.has(invite.inviteId),
      );
      return fresh.length ? [...queue, ...fresh] : queue;
    });
  };

  useEffect(() => {
    if (!webSocketStompClient || !userProfile) return;
    const subscribed = webSocketStompClient.subscribe(
      `/${userProfile.id}/duel/invite`,
      (message) => {
        let payload: any;
        try {
          payload = JSON.parse(message.body);
        } catch (e) {
          return;
        }
        const eventType = payload?.eventType;
        if (eventType === "INVITE_RECEIVED") {
          // Append (de-duped by inviteId) so concurrent invites stack instead of overwriting.
          enqueueInvites([payload]);
          try {
            duelSounds.inviteReceived();
          } catch (e) {
            // audio unavailable (e.g. no user gesture yet) — non-fatal
          }
        } else if (
          eventType === "INVITE_CANCELLED" ||
          eventType === "INVITE_ACCEPTED" ||
          eventType === "INVITE_EXPIRED"
        ) {
          // Cancelled by the inviter, or accepted/expired on another of this user's tabs → drop it.
          setInviteQueue((queue) =>
            queue.filter((invite) => invite.inviteId !== payload.inviteId),
          );
        } else if (eventType === "INVITE_DECLINED") {
          // Received by the INVITER: the peer declined the challenge/watch invite.
          NotificationManager.info(i18n.t("invite_declined"));
        }
      },
    );
    // Recover invites pushed while this subscription wasn't live yet (reconnect / tab-hide / just-mounted
    // gap). A duel invite is a single un-acked STOMP push, so a missed one is otherwise lost with no retry —
    // the reported symptom where the first invite "didn't arrive" but a re-send seconds later did. Runs on
    // every (re)subscribe; the queue de-dupes so an invite that also arrives live is not shown twice.
    let cancelled = false;
    retrievePendingDuelInvites().then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return;
      enqueueInvites(data);
    });
    return () => {
      cancelled = true;
      subscribed.unsubscribe();
    };
  }, [webSocketStompClient, userProfile]);

  // Friendship + presence events on the per-user topic. Unlike a duel invite, a friend request is durable:
  // it waits in the İstekler tab, so it gets a plain toast and a badge rather than a countdown card.
  // Everything else is rebroadcast as a window event for the panel to patch its rows, so a status change
  // costs no refetch and the subscription doesn't have to know whether the panel is even mounted.
  useEffect(() => {
    if (!webSocketStompClient || !userProfile) return;
    const subscribed = webSocketStompClient.subscribe(
      `/${userProfile.id}/friends`,
      (message) => {
        let payload: FriendEventPayload;
        try {
          payload = JSON.parse(message.body);
        } catch (e) {
          return;
        }
        if (payload?.eventType === "REQUEST_RECEIVED") {
          NotificationManager.info(
            payload.counterpartName
              ? `${payload.counterpartName} ${i18n.t("friend_request_received_tail")}`
              : i18n.t("friend_request_received"),
          );
          setIncomingRequestCount((count) => count + 1);
        } else if (payload?.eventType === "REQUEST_ACCEPTED") {
          NotificationManager.success(
            payload.counterpartName
              ? `${payload.counterpartName} ${i18n.t("friend_request_accepted_tail")}`
              : i18n.t("friend_request_accepted"),
          );
        } else if (payload?.eventType === "FRIEND_REMOVED") {
          // Counting up alone never brought the badge back down — a requester who withdrew left it lit.
          // A removal can only LOWER the count, so the server's figure is taken only when it is smaller:
          // opening the panel zeroes the badge, and a plain re-seed would re-light requests already seen.
          seedIncomingRequestCount({ lowerOnly: true });
        }
        window.dispatchEvent(new CustomEvent(FRIEND_EVENT, { detail: payload }));
      },
    );
    // Seed the badge from the server: requests that arrived while this tab was closed are the normal case,
    // and unlike a 30s duel invite they are still waiting.
    let cancelled = false;
    const seedIncomingRequestCount = ({ lowerOnly = false } = {}) =>
      retrievePendingFriendRequests().then(({ data }) => {
        if (cancelled || !data) return;
        const serverCount = ((data as any).incomingList || []).length;
        setIncomingRequestCount((count) =>
          lowerOnly ? Math.min(count, serverCount) : serverCount,
        );
      });
    seedIncomingRequestCount();
    return () => {
      cancelled = true;
      subscribed.unsubscribe();
    };
  }, [webSocketStompClient, userProfile]);

  // Let any screen (e.g. the duel waiting room, where the floating button is hidden) open this panel.
  useEffect(() => {
    const open = () => setPanelOpen(true);
    window.addEventListener(OPEN_FRIENDS_PANEL_EVENT, open);
    return () => window.removeEventListener(OPEN_FRIENDS_PANEL_EVENT, open);
  }, []);

  // Opening the panel is the learner acting on the requests, so stop nagging with the badge.
  useEffect(() => {
    if (panelOpen) {
      setIncomingRequestCount(0);
    }
  }, [panelOpen]);

  const handleAcceptInvite = async () => {
    const queue = inviteQueueRef.current;
    const invite = queue[0];
    if (!invite) return;
    // Accepting commits us to this duel — auto-decline every other queued invite so those inviters get
    // an immediate "declined" instead of waiting for the TTL. Clear the whole queue up front.
    const others = queue.slice(1);
    setInviteQueue([]);
    others.forEach((o) => declineDuelInvite(o.inviteId));

    const { status, err } = await acceptDuelInvite(invite.inviteId);
    if (err || status === 410) {
      NotificationManager.info(i18n.t("invite_expired"));
      return;
    }
    const joinParams = {
      matchCode: invite.matchCode,
      userId: userInformation.id,
      userProfileId: userProfile.id,
    };
    if (invite.kind === "SPECTATOR") {
      const { data, err: spectateErr } = await spectateDuelByCode(joinParams);
      if (spectateErr) {
        NotificationManager.error(i18n.t("duel_no_longer_available"));
        return;
      }
      navigate(`/duels/${(data as any).id}/spectate`);
    } else {
      const { data, err: joinErr } = await joinDuelByCode(joinParams);
      if (joinErr) {
        NotificationManager.error(i18n.t("duel_no_longer_available"));
        return;
      }
      navigate(`/duels/${(data as any).id}`);
    }
  };

  // Decline only the front invite and reveal the next one queued behind it.
  const handleDeclineInvite = () => {
    const invite = inviteQueueRef.current[0];
    setInviteQueue((queue) => queue.slice(1));
    if (invite) {
      declineDuelInvite(invite.inviteId);
    }
  };

  // Countdown ran out on the front invite → drop it (already expired server-side) and show the next.
  const handleExpireInvite = () => {
    setInviteQueue((queue) => queue.slice(1));
  };

  // Hide the floating button on the in-duel screens (waiting room / play / spectate) so it never
  // overlaps the match chrome; the invite listener stays active everywhere regardless.
  const onDuelScreen = /^\/duels\/.+/.test(location.pathname);

  // Do not pop an invite card over a question. The inviter's client already disables the button and the
  // server refuses the invite, but both of those depend on the inviter seeing a fresh status — this is the
  // guard that holds regardless. Held invites stay queued and surface when the learner leaves the solver;
  // most simply lapse against their 30s TTL, which is the right outcome for someone mid-question.
  const holdInvites = statusForPathname(location.pathname) !== "ONLINE" && !onDuelScreen;
  const currentInvite = holdInvites ? null : inviteQueue[0];

  return (
    <>
      <style>{duelSocialStyles + friendPanelStyles}</style>
      {!onDuelScreen ? (
        // Icon only: the label ("Çevrimiçi arkadaşlar") made a wide pill that ate the
        // bottom-right corner it shares with the mascot. The name lives on in the
        // tooltip and the accessible name, and in the drawer's own heading.
        <button
          type="button"
          className="dso-fab"
          onClick={() => setPanelOpen(true)}
          aria-label={i18n.t("friends_online")}
          title={i18n.t("friends_online")}
        >
          <Icon name="users" size={24} />
          {incomingRequestCount > 0 ? (
            <span className="dso-fab-badge">{incomingRequestCount}</span>
          ) : null}
        </button>
      ) : null}

      {panelOpen ? (
        <FriendsOnlinePanel onClose={() => setPanelOpen(false)} />
      ) : null}

      {currentInvite ? (
        <DuelInviteToast
          key={currentInvite.inviteId}
          invite={currentInvite}
          queueCount={inviteQueue.length}
          onAccept={handleAcceptInvite}
          onDecline={handleDeclineInvite}
          onExpire={handleExpireInvite}
        />
      ) : null}
    </>
  );
};

export default DuelSocialLayer;
